import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import LazyImage from '../../components/ui/LazyImage';

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 5;

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const q = query(collection(db, 'blog_categories'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      const cats = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategories(cats);
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      // Buscar todos os posts ordenados e filtrar em memória para evitar erros de Índice Composto no Firebase
      const q = query(
        collection(db, 'blog_posts'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      let postsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filtrar apenas publicados
      postsList = postsList.filter(post => post.status === 'published');

      // Filtrar por categoria se houver
      if (selectedCategory) {
        postsList = postsList.filter(post => post.categoryId === selectedCategory);
      }

      setPosts(postsList);
      setCurrentPage(1);
    } catch (error) {
      console.error("Erro ao buscar posts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(posts.length / postsPerPage);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-7xl mx-auto mb-6">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium">
          <FiArrowLeft /> Voltar para o Início
        </Link>
      </div>
      
      <main className="flex-grow pb-12 sm:pb-16 lg:pb-24">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight break-words">
              Blog CriaApp
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
              Dicas, tutoriais e novidades sobre desenvolvimento de aplicativos, tecnologia e negócios.
            </p>
          </div>

          {/* Filtros de Categoria */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <button 
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!selectedCategory ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}
            >
              Todos
            </button>
            {categories.map(cat => (
              <button 
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === cat.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : currentPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {currentPosts.map(post => (
                <article key={post.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group flex flex-col">
                  {post.coverImage && (
                    <Link to={`/blog/${post.slug}`} className="block overflow-hidden h-48">
                      <LazyImage 
                        src={post.coverImage} 
                        alt={post.title} 
                        containerClassName="w-full h-full"
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 absolute inset-0"
                      />
                    </Link>
                  )}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center gap-2 mb-3">
                      {post.categoryName && (
                        <span className="bg-blue-50 text-blue-600 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                          {post.categoryName}
                        </span>
                      )}
                      <span className="text-gray-400 text-xs">
                        {new Date(post.createdAt?.seconds * 1000).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <Link to={`/blog/${post.slug}`}>
                      <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                    </Link>
                    <p className="text-gray-600 mb-4 line-clamp-3 text-sm flex-grow">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-50">
                      <div className="flex items-center gap-2">
                        {post.authorAvatar ? (
                          <LazyImage src={post.authorAvatar} alt={post.authorName} containerClassName="w-8 h-8 rounded-full" className="w-full h-full rounded-full object-cover absolute inset-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xs">
                            {post.authorName?.charAt(0) || 'A'}
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-700">{post.authorName || 'Autor'}</span>
                      </div>
                      <Link to={`/blog/${post.slug}`} className="text-blue-600 text-sm font-semibold hover:text-blue-800">
                        Ler mais →
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
              </div>
              
              {totalPages > 1 && (
                <div className="flex justify-center mt-12 gap-2">
                  <button
                    onClick={() => {
                      setCurrentPage(prev => Math.max(prev - 1, 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 font-medium disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  >
                    Anterior
                  </button>
                  <div className="flex items-center gap-1 hidden sm:flex">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setCurrentPage(i + 1);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-10 h-10 rounded-lg font-medium transition-colors ${currentPage === i + 1 ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setCurrentPage(prev => Math.min(prev + 1, totalPages));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 font-medium disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  >
                    Próximo
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 sm:py-20 px-4 bg-white rounded-2xl border border-gray-100 shadow-sm max-w-3xl mx-auto mt-8">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Nenhum artigo encontrado</h3>
              <p className="text-sm sm:text-base text-gray-500">Tente buscar por outra categoria ou volte mais tarde.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BlogList;
